import { useAuth } from "@/context/AuthContext";
import { VerifyEmailNotice } from "@/components/VerifyEmail";

// Client-side mirror of the backend route guard: pages whose addresses now refuse unsigned (or non-admin) callers
// show a sign-in prompt instead of a wall of 401 errors. The backend remains the real enforcement point.
export function AccessGate({ level = "signed_in", children }) {
  const { user } = useAuth();
  if (user === undefined) {
    return <div data-testid="access-gate-checking" className="min-h-screen bg-[#faf9f6]" />;
  }
  if (!user) {
    return (
      <Shell testId="access-gate-signin" kicker="Sign in required" title="Please sign in to continue"
             body="This part of The Writing Studio is available to signed-in students and educators.">
        <a href="/student" data-testid="access-gate-student-link" className="inline-flex items-center bg-[#8C3A2A] text-stone-50 px-7 py-3.5 rounded-sm font-mono-panel text-xs uppercase tracking-[0.2em] hover:bg-[#a34635] transition-colors">Student sign in</a>
        <a href="/teacher" data-testid="access-gate-teacher-link" className="inline-flex items-center border border-stone-400 text-stone-800 px-7 py-3.5 rounded-sm font-mono-panel text-xs uppercase tracking-[0.2em] hover:bg-stone-100 transition-colors">Educator sign in</a>
      </Shell>
    );
  }
  if (user.email_verified === false) {
    return <VerifyEmailNotice />;
  }
  if (level === "admin" && user.role !== "admin") {
    return (
      <Shell testId="access-gate-admin" kicker="Administrators only" title="This page is closed"
             body="It is a developer tool that only an administrator account may open.">
        <a href="/" data-testid="access-gate-home-link" className="inline-flex items-center border border-stone-400 text-stone-800 px-7 py-3.5 rounded-sm font-mono-panel text-xs uppercase tracking-[0.2em] hover:bg-stone-100 transition-colors">Back to the portal</a>
      </Shell>
    );
  }
  return children;
}

function Shell({ testId, kicker, title, body, children }) {
  return (
    <div data-testid={testId} className="min-h-screen flex flex-col items-center justify-center bg-[#faf9f6] px-6 text-center">
      <p className="font-mono-panel text-[11px] uppercase tracking-[0.22em] text-[#8C3A2A] mb-3">{kicker}</p>
      <h1 className="font-serif-display text-3xl sm:text-4xl text-stone-900 mb-3 max-w-xl leading-tight">{title}</h1>
      <p className="text-[15px] text-stone-600 max-w-md mb-8">{body}</p>
      <div className="flex flex-wrap gap-3 justify-center">{children}</div>
    </div>
  );
}
